import React, { useEffect, useMemo, useState } from "react";
import "../../assets/css/api-documentation.css";

type ApiSchema = {
  $ref?: string;
  type?: string;
  format?: string;
  description?: string;
  nullable?: boolean;
  required?: string[];
  properties?: Record<string, ApiSchema>;
  items?: ApiSchema;
  allOf?: ApiSchema[];
  example?: unknown;
  default?: unknown;
  enum?: unknown[];
  minimum?: number;
  maximum?: number;
};

type ApiMedia = {
  schema?: ApiSchema;
  example?: unknown;
  examples?: Record<string, { value?: unknown }>;
};

type ApiParameter = {
  name?: string;
  in?: string;
  required?: boolean;
  description?: string;
  example?: unknown;
  schema?: ApiSchema;
  $ref?: string;
};

type ApiResponse = {
  description?: string;
  $ref?: string;
  headers?: Record<string, { description?: string; schema?: ApiSchema }>;
  content?: Record<string, ApiMedia>;
};

type ApiRequestBody = {
  required?: boolean;
  description?: string;
  content?: Record<string, ApiMedia>;
  $ref?: string;
};

type ApiOperation = {
  tags?: string[];
  summary?: string;
  description?: string;
  deprecated?: boolean;
  security?: Array<Record<string, string[]>>;
  parameters?: ApiParameter[];
  requestBody?: ApiRequestBody;
  responses?: Record<string, ApiResponse>;
};

type OpenApiSpec = {
  info: { title: string; version: string; description?: string };
  servers?: { url: string; description?: string }[];
  tags?: { name: string; description?: string }[];
  paths: Record<string, Record<string, ApiOperation>>;
  components?: {
    parameters?: Record<string, ApiParameter>;
    responses?: Record<string, ApiResponse>;
    schemas?: Record<string, ApiSchema>;
  };
};

type Endpoint = ApiOperation & { method: string; path: string };
type SchemaRow = { name: string; type: string; required: boolean; description: string };

const backendUrl = process.env.REACT_APP_BACKEND_URL ||
  (process.env.NODE_ENV === "production" ? window.location.origin : "http://localhost:5000");
const specUrl = `${backendUrl}/api/v1/openapi.json`;
const documentationPath = "/api-documentation";
const postmanUrl = "https://geotrackin-app-8314215.postman.co/workspace/GeoTracking-API-V1~4370b3f5-e9e1-487e-b3d9-fc71ddeb10cd/collection/51228388-f2e933b2-e4eb-41ed-83e9-95e6648e01f8";

const fallbackSpec: OpenApiSpec = {
  info: {
    title: "GeoTracking External API",
    version: "1.0.0",
    description: "Connectez une application mobile, une plateforme métier ou votre serveur aux données de flotte GeoTracking avec une clé API simple et sécurisée.",
  },
  servers: [{ url: "https://geotrackin.com/api/v1", description: "Production" }],
  tags: [
    { name: "Authentication", description: "Génération et renouvellement de la clé API." },
    { name: "Vehicles", description: "Véhicules et données associées." },
    { name: "Drivers", description: "Conducteurs et affectations." },
    { name: "Groups", description: "Groupes de véhicules et de conducteurs." },
    { name: "Statistics", description: "Totaux et indicateurs du compte." },
    { name: "Positions", description: "Positions GPS en temps réel et historique." },
  ],
  paths: {
    "/api-key/generate": {
      post: {
        tags: ["Authentication"],
        summary: "Générer ou renouveler la clé API",
        description: "Génère une nouvelle clé à partir des identifiants du compte.",
        requestBody: {
          required: true,
          content: {
            "application/json": {
              example: { username: "client@example.com", password: "••••••••" },
              schema: { type: "object", required: ["username", "password"], properties: { username: { type: "string" }, password: { type: "string", format: "password" } } },
            },
          },
        },
        responses: { 200: { description: "Clé générée.", content: { "application/json": { example: { success: true, api_key: "gt_example_api_key" } } } }, 401: { description: "Identifiants invalides." } },
      },
    },
    "/vehicles": { get: { tags: ["Vehicles"], summary: "Lister les véhicules", description: "Retourne les véhicules actifs avec leur groupe et leur conducteur principal.", security: [{ apiKeyAuth: [] }], responses: { 200: { description: "Page de véhicules." }, 401: { description: "Clé API invalide." } } } },
    "/vehicles/{vehicleId}": { get: { tags: ["Vehicles"], summary: "Consulter un véhicule", description: "Retourne le détail d’un véhicule du compte.", security: [{ apiKeyAuth: [] }], responses: { 200: { description: "Véhicule trouvé." }, 404: { description: "Véhicule introuvable." } } } },
    "/vehicles/{vehicleId}/drivers": { get: { tags: ["Drivers"], summary: "Lister les conducteurs d’un véhicule", description: "Retourne les conducteurs affectés au véhicule.", security: [{ apiKeyAuth: [] }], responses: { 200: { description: "Conducteurs affectés." } } } },
    "/drivers": { get: { tags: ["Drivers"], summary: "Lister les conducteurs", description: "Retourne les conducteurs actifs du compte.", security: [{ apiKeyAuth: [] }], responses: { 200: { description: "Page de conducteurs." } } } },
    "/vehicle-groups": { get: { tags: ["Groups"], summary: "Lister les groupes de véhicules", description: "Retourne les groupes et leur nombre de véhicules.", security: [{ apiKeyAuth: [] }], responses: { 200: { description: "Groupes de véhicules." } } } },
    "/driver-groups": { get: { tags: ["Groups"], summary: "Lister les groupes de conducteurs", description: "Retourne les groupes et leur nombre de conducteurs.", security: [{ apiKeyAuth: [] }], responses: { 200: { description: "Groupes de conducteurs." } } } },
    "/summary": { get: { tags: ["Statistics"], summary: "Récupérer les totaux du compte", description: "Retourne les totaux de véhicules, conducteurs et groupes.", security: [{ apiKeyAuth: [] }], responses: { 200: { description: "Totaux disponibles." } } } },
    "/positions": { get: { tags: ["Positions"], summary: "Récupérer les positions en temps réel", description: "Retourne la dernière position GPS connue de chaque véhicule.", security: [{ apiKeyAuth: [] }], responses: { 200: { description: "Positions disponibles." } } } },
    "/positions/history": { get: { tags: ["Positions"], summary: "Récupérer l’historique de position", description: "Retourne l’itinéraire GPS d’un véhicule sur une période.", security: [{ apiKeyAuth: [] }], responses: { 200: { description: "Historique disponible." }, 400: { description: "Période invalide." } } } },
  },
};

const refKey = (ref?: string) => ref?.split("/").pop() || "";

const resolveParameter = (parameter: ApiParameter, spec: OpenApiSpec) =>
  parameter.$ref ? spec.components?.parameters?.[refKey(parameter.$ref)] || parameter : parameter;

const resolveResponse = (response: ApiResponse, spec: OpenApiSpec) =>
  response.$ref ? spec.components?.responses?.[refKey(response.$ref)] || response : response;

const resolveSchema = (schema: ApiSchema | undefined, spec: OpenApiSpec): ApiSchema | undefined =>
  schema?.$ref ? spec.components?.schemas?.[refKey(schema.$ref)] || schema : schema;

const schemaExample = (schema: ApiSchema | undefined, spec: OpenApiSpec, depth = 0): unknown => {
  if (!schema || depth > 6) return undefined;
  const resolved = resolveSchema(schema, spec) || schema;
  if (resolved.example !== undefined) return resolved.example;
  if (resolved.default !== undefined) return resolved.default;
  if (resolved.enum?.length) return resolved.enum[0];
  if (resolved.allOf?.length) {
    return resolved.allOf.reduce<Record<string, unknown>>((result, item) => {
      const example = schemaExample(item, spec, depth + 1);
      return example && typeof example === "object" && !Array.isArray(example) ? { ...result, ...example as Record<string, unknown> } : result;
    }, {});
  }
  if (resolved.type === "array" || resolved.items) return [schemaExample(resolved.items, spec, depth + 1)];
  if (resolved.type === "object" || resolved.properties) {
    return Object.fromEntries(Object.entries(resolved.properties || {}).map(([name, property]) => [name, schemaExample(property, spec, depth + 1)]));
  }
  if (resolved.format === "date-time") return "2026-08-06T13:00:00.000Z";
  if (resolved.format === "date") return "2026-08-06";
  if (resolved.format === "email") return "client@example.com";
  if (resolved.format === "password") return "••••••••";
  if (resolved.type === "boolean") return true;
  if (resolved.type === "integer") return 1;
  if (resolved.type === "number") return 3.0588;
  return "string";
};

const mediaExample = (media: ApiMedia | undefined, spec: OpenApiSpec) => {
  if (!media) return undefined;
  if (media.example !== undefined) return media.example;
  const namedExample = Object.values(media.examples || {}).find((example) => example.value !== undefined);
  return namedExample?.value ?? schemaExample(media.schema, spec);
};

const mediaJson = (content?: Record<string, ApiMedia>) =>
  content?.["application/json"] || content?.[Object.keys(content || {})[0]];

const schemaType = (schema: ApiSchema | undefined, spec: OpenApiSpec): string => {
  const resolved = resolveSchema(schema, spec) || schema;
  if (!resolved) return "unknown";
  if (resolved.allOf) return "object";
  if (resolved.type === "array") return `array<${schemaType(resolved.items, spec)}>`;
  return `${resolved.type || (resolved.properties ? "object" : "unknown")}${resolved.format ? ` (${resolved.format})` : ""}${resolved.nullable ? " · nullable" : ""}`;
};

const schemaRows = (schema: ApiSchema | undefined, spec: OpenApiSpec): SchemaRow[] => {
  const rows: SchemaRow[] = [];
  const visit = (candidate: ApiSchema | undefined, prefix = "", depth = 0) => {
    if (!candidate || depth > 2) return;
    const resolved = resolveSchema(candidate, spec) || candidate;
    if (resolved.allOf) {
      resolved.allOf.forEach((item) => visit(item, prefix, depth));
      return;
    }
    const required = new Set(resolved.required || []);
    Object.entries(resolved.properties || {}).forEach(([name, property]) => {
      const fieldName = `${prefix}${name}`;
      rows.push({ name: fieldName, type: schemaType(property, spec), required: required.has(name), description: property.description || "—" });
      const propertySchema = resolveSchema(property, spec) || property;
      if (propertySchema.type === "array") visit(propertySchema.items, `${fieldName}[].`, depth + 1);
      else if (propertySchema.properties || propertySchema.allOf || propertySchema.$ref) visit(propertySchema, `${fieldName}.`, depth + 1);
    });
  };
  visit(schema);
  return Array.from(new Map(rows.map((row) => [row.name, row])).values()).slice(0, 60);
};

const parameterValue = (parameter: ApiParameter) => {
  const examples: Record<string, unknown> = {
    page: 1,
    limit: parameter.name === "limit" ? 25 : 1,
    search: "00123-116-16",
    groupId: 3,
    vehicleId: 125,
    driverId: 9,
    deviceId: "864636065361202",
    immatriculation: "00123-116-16",
    dateStart: "2026-08-06T12:00:00.000Z",
    dateEnd: "2026-08-06T13:00:00.000Z",
  };
  return parameter.example ?? parameter.schema?.example ?? parameter.schema?.default ?? examples[parameter.name || ""] ?? "string";
};

const buildRequestUrl = (endpoint: Endpoint, parameters: ApiParameter[], baseUrl: string, protectedEndpoint: boolean) => {
  let path = endpoint.path;
  parameters.filter((parameter) => parameter.in === "path").forEach((parameter) => {
    path = path.replace(`{${parameter.name}}`, String(parameterValue(parameter)));
  });
  const query = parameters
    .filter((parameter) => parameter.in === "query")
    .map((parameter) => `${parameter.name}=${parameterValue(parameter)}`);
  if (protectedEndpoint) query.push("api_key={{apiKey}}");
  return `${baseUrl}${path}${query.length ? `?${query.join("&")}` : ""}`;
};

const buildCurl = (endpoint: Endpoint, url: string, body: unknown) => {
  const lines = [`curl --request ${endpoint.method} \\`, `  --url '${url}' \\`, "  --header 'Accept: application/json'" ];
  if (body !== undefined) {
    lines[lines.length - 1] += " \\";
    lines.push("  --header 'Content-Type: application/json' \\");
    lines.push(`  --data '${JSON.stringify(body, null, 2)}'`);
  }
  return lines.join("\n");
};

const formatJson = (value: unknown) => JSON.stringify(value, null, 2);

const CopyButton = ({ value }: { value: string }) => {
  const [copied, setCopied] = useState(false);
  const copy = async () => {
    await navigator.clipboard.writeText(value);
    setCopied(true);
    window.setTimeout(() => setCopied(false), 1400);
  };
  return <button className="api-docs__copy" onClick={copy}>{copied ? "Copié" : "Copier"}</button>;
};

const JsonCard = ({ label, value }: { label: string; value: unknown }) => {
  const json = formatJson(value);
  return <div className="api-docs__code-card"><div className="api-docs__code-header"><span>{label}</span><CopyButton value={json} /></div><pre><code>{json}</code></pre></div>;
};

const FleetManagementDocumentation = () => {
  const [spec, setSpec] = useState<OpenApiSpec>(fallbackSpec);
  const [search, setSearch] = useState("");
  const [menuOpen, setMenuOpen] = useState(false);
  const [activeSection, setActiveSection] = useState("introduction");

  useEffect(() => {
    fetch(specUrl, { credentials: "omit" })
      .then(async (response) => {
        if (!response.ok) throw new Error(`HTTP ${response.status}`);
        return response.json();
      })
      .then(setSpec)
      .catch(() => setSpec(fallbackSpec));
  }, []);

  useEffect(() => {
    const sections = document.querySelectorAll<HTMLElement>(".api-docs [data-doc-section]");
    const observer = new IntersectionObserver((entries) => entries.forEach((entry) => entry.isIntersecting && setActiveSection(entry.target.id)), { rootMargin: "-15% 0px -72%" });
    sections.forEach((section) => observer.observe(section));
    return () => observer.disconnect();
  }, [spec]);

  const endpoints = useMemo<Endpoint[]>(() => Object.entries(spec.paths).flatMap(([path, operations]) =>
    Object.entries(operations)
      .filter(([method]) => ["get", "post", "put", "patch", "delete"].includes(method))
      .map(([method, operation]) => ({ ...operation, method: method.toUpperCase(), path }))
  ), [spec]);

  const filteredEndpoints = useMemo(() => {
    const query = search.trim().toLowerCase();
    if (!query) return endpoints;
    return endpoints.filter((endpoint) => [endpoint.method, endpoint.path, endpoint.summary, endpoint.description, ...(endpoint.tags || [])].filter(Boolean).join(" ").toLowerCase().includes(query));
  }, [endpoints, search]);

  const apiBaseUrl = spec.servers?.[0]?.url || `${backendUrl}/api/v1`;
  const curlExample = buildCurl({ method: "GET", path: "/vehicles" }, `${apiBaseUrl}/vehicles?page=1&limit=25&api_key={{apiKey}}`, undefined);
  const navClass = (section: string) => activeSection === section ? "active" : "";
  const closeMenu = () => setMenuOpen(false);

  return (
    <div className="api-docs" id="top">
      <header className="api-docs__mobile-header"><img src="/asset/images/icons/logo_light.png" alt="GeoTracking" /><button type="button" onClick={() => setMenuOpen((open) => !open)} aria-label="Ouvrir le menu">☰</button></header>

      <aside className={`api-docs__sidebar${menuOpen ? " open" : ""}`}>
        <a className="api-docs__brand" href={`${documentationPath}#top`} onClick={closeMenu}><img src="/asset/images/icons/logo_light.png" alt="GeoTracking" /><span>V1</span></a>
        <input className="api-docs__sidebar-search" type="search" value={search} onChange={(event) => setSearch(event.target.value)} placeholder="Rechercher un endpoint…" aria-label="Rechercher dans la documentation" />
        <nav className="api-docs__nav" aria-label="Documentation API">
          <p className="api-docs__nav-title">Prise en main</p>
          <a className={navClass("introduction")} href={`${documentationPath}#introduction`} onClick={closeMenu}><span />Introduction</a>
          <a className={navClass("authentication")} href={`${documentationPath}#authentication`} onClick={closeMenu}><span />Authentification</a>
          <a className={navClass("conventions")} href={`${documentationPath}#conventions`} onClick={closeMenu}><span />Headers et format</a>
          <a className={navClass("errors")} href={`${documentationPath}#errors`} onClick={closeMenu}><span />Erreurs</a>
          <a className={navClass("postman")} href={`${documentationPath}#postman`} onClick={closeMenu}><span />Collection Postman</a>
          <p className="api-docs__nav-title">Référence</p>
          {(spec.tags || []).map((tag) => <a className={navClass(`tag-${tag.name}`)} key={tag.name} href={`${documentationPath}#tag-${tag.name}`} onClick={closeMenu}><span />{tag.name}</a>)}
        </nav>
      </aside>

      {menuOpen && <button className="api-docs__overlay" aria-label="Fermer le menu" onClick={closeMenu} />}

      <div className="api-docs__content">
        <header className="api-docs__hero">
          <p className="api-docs__eyebrow">Documentation développeur officielle</p>
          <h1>{spec.info.title}</h1>
          <p>{spec.info.description}</p>
          <div className="api-docs__hero-actions">
            <a className="api-docs__button api-docs__button--primary" href={`${documentationPath}#authentication`}>Commencer</a>
            <a className="api-docs__button" href={postmanUrl} target="_blank" rel="noreferrer">Tester dans Postman</a>
            <a className="api-docs__button" href={specUrl} target="_blank" rel="noreferrer">OpenAPI JSON</a>
            <span className="api-docs__button">Version {spec.info.version}</span>
          </div>
        </header>

        <main className="api-docs__main">
          <section className="api-docs__section" id="introduction" data-doc-section>
            <h2>Bienvenue dans l’API GeoTracking V1</h2>
            <p className="api-docs__lead">Une référence complète pour intégrer les véhicules, conducteurs, groupes, statistiques et positions GPS. Le contrat ci-dessous est généré depuis la même spécification OpenAPI que la collection Postman.</p>
            <div className="api-docs__facts">
              <div><span>URL de production</span><code>{apiBaseUrl}</code></div>
              <div><span>Format</span><strong>JSON · UTF-8</strong></div>
              <div><span>Authentification</span><strong>Clé API</strong></div>
              <div><span>Transport</span><strong>HTTPS</strong></div>
            </div>
          </section>

          <section className="api-docs__section" id="authentication" data-doc-section>
            <h2>Authentification</h2>
            <p className="api-docs__lead">La génération de clé utilise une requête <strong>POST</strong>. Les ressources de consultation utilisent <strong>GET</strong>. Aucun JWT n’est nécessaire.</p>
            <div className="api-docs__steps">
              <article className="api-docs__step"><span>1</span><h3>Générer la clé</h3><p><code>POST /api/v1/api-key/generate</code> avec un body JSON.</p></article>
              <article className="api-docs__step"><span>2</span><h3>Conserver la clé</h3><p>Stockez <code>api_key</code> dans un secret serveur ou un stockage mobile sécurisé.</p></article>
              <article className="api-docs__step"><span>3</span><h3>Appeler l’API</h3><p>Ajoutez <code>api_key={"{{apiKey}}"}</code> à la query string.</p></article>
            </div>
            <div className="api-docs__notice"><strong>Sécurité :</strong> ne publiez jamais de véritables identifiants ou clés API dans Git, Postman public ou le code d’une application.</div>
          </section>

          <section className="api-docs__section" id="conventions" data-doc-section>
            <h2>Headers et conventions</h2>
            <p className="api-docs__lead">Les requêtes et réponses utilisent JSON. Les dates sont exprimées au format ISO 8601 et les listes paginées exposent toujours les totaux.</p>
            <div className="api-docs__table-wrap"><table><thead><tr><th>Header / paramètre</th><th>Valeur</th><th>Quand l’utiliser</th></tr></thead><tbody>
              <tr><td><code>Accept</code></td><td><code>application/json</code></td><td>Toutes les requêtes</td></tr>
              <tr><td><code>Content-Type</code></td><td><code>application/json</code></td><td>Requêtes avec body, notamment la génération de clé</td></tr>
              <tr><td><code>api_key</code></td><td><code>{"{{apiKey}}"}</code></td><td>Query parameter de chaque endpoint protégé</td></tr>
              <tr><td><code>dateStart / dateEnd</code></td><td><code>2026-08-06T13:00:00.000Z</code></td><td>Historique GPS, format ISO 8601</td></tr>
            </tbody></table></div>
            <div className="api-docs__code-card"><div className="api-docs__code-header"><span>Premier appel cURL</span><CopyButton value={curlExample} /></div><pre><code>{curlExample}</code></pre></div>
          </section>

          <section className="api-docs__section" id="errors" data-doc-section>
            <h2>Format des erreurs</h2>
            <p className="api-docs__lead">Toutes les erreurs utilisent une structure JSON stable. Le code HTTP indique la catégorie et le champ <code>code</code> permet le traitement applicatif.</p>
            <div className="api-docs__error-layout">
              <div className="api-docs__responses"><span className="api-docs__response"><strong>400</strong> Paramètres invalides</span><span className="api-docs__response"><strong>401</strong> Clé absente ou invalide</span><span className="api-docs__response"><strong>403</strong> Accès interdit</span><span className="api-docs__response"><strong>404</strong> Ressource introuvable</span><span className="api-docs__response"><strong>500</strong> Erreur interne</span></div>
              <JsonCard label="Exemple · 401" value={{ success: false, code: "API_KEY_INVALID", message: "Invalid API key." }} />
            </div>
          </section>

          <section className="api-docs__section" id="postman" data-doc-section>
            <h2>Collection officielle Postman</h2>
            <p className="api-docs__lead">Postman est l’environnement de test. Cette page reste la documentation de référence : elle détaille le contrat, les headers, les schémas et tous les retours HTTP.</p>
            <div className="api-docs__steps">
              <article className="api-docs__step"><span>1</span><h3>Recevoir l’invitation</h3><p>Accédez au workspace partenaire avec votre adresse professionnelle.</p></article>
              <article className="api-docs__step"><span>2</span><h3>Générer la clé</h3><p>Le script Postman enregistre la réponse dans <code>{"{{apiKey}}"}</code>.</p></article>
              <article className="api-docs__step"><span>3</span><h3>Tester</h3><p>Les requêtes utilisent automatiquement <code>baseUrl</code> et <code>apiKey</code>.</p></article>
            </div>
            <a className="api-docs__postman-link" href={postmanUrl} target="_blank" rel="noreferrer">Accéder à la collection Postman</a>
          </section>

          <section className="api-docs__section" id="reference" data-doc-section>
            <div className="api-docs__reference-header"><div><h2>Référence complète des endpoints</h2><p className="api-docs__lead">Chaque opération affiche sa méthode, ses headers, paramètres, body, résultats HTTP, schéma et exemples JSON.</p></div><span className="api-docs__count">{filteredEndpoints.length} endpoint{filteredEndpoints.length > 1 ? "s" : ""}</span></div>

            {(spec.tags || []).map((tag) => {
              const tagged = filteredEndpoints.filter((endpoint) => endpoint.tags?.includes(tag.name));
              if (!tagged.length) return null;
              return <section className="api-docs__tag-section" id={`tag-${tag.name}`} data-doc-section key={tag.name}>
                <h2>{tag.name}</h2><p className="api-docs__tag-description">{tag.description}</p>
                {tagged.map((endpoint) => {
                  const parameters = (endpoint.parameters || []).map((parameter) => resolveParameter(parameter, spec));
                  const protectedEndpoint = Boolean(endpoint.security?.length) || endpoint.path !== "/api-key/generate";
                  const requestMedia = mediaJson(endpoint.requestBody?.content);
                  const requestExample = mediaExample(requestMedia, spec);
                  const requestFields = schemaRows(requestMedia?.schema, spec);
                  const requestUrl = buildRequestUrl(endpoint, parameters, apiBaseUrl, protectedEndpoint);
                  const endpointCurl = buildCurl(endpoint, requestUrl, requestExample);
                  const responseEntries = Object.entries(endpoint.responses || {}).map(([status, rawResponse]) => ({ status, response: resolveResponse(rawResponse, spec) }));
                  const successResponse = responseEntries.find(({ status }) => status.startsWith("2"))?.response;
                  const successMedia = mediaJson(successResponse?.content);
                  const successExample = mediaExample(successMedia, spec);
                  const fields = schemaRows(successMedia?.schema, spec);

                  return <article className="api-docs__endpoint" key={`${endpoint.method}-${endpoint.path}`}>
                    <header className="api-docs__endpoint-header">
                      <div className="api-docs__endpoint-heading">
                        <h3>{endpoint.summary}</h3>
                        <div className="api-docs__endpoint-signature"><span className={`api-docs__method api-docs__method--${endpoint.method.toLowerCase()}`}>{endpoint.method}</span><code className="api-docs__path">{endpoint.path}</code></div>
                      </div>
                      {endpoint.deprecated && <span className="api-docs__deprecated">HISTORIQUE</span>}
                    </header>
                    <div className="api-docs__endpoint-body">
                      <div className="api-docs__endpoint-docs">
                        <p className="api-docs__endpoint-description">{endpoint.description}</p>
                        <div className="api-docs__endpoint-meta"><span className="api-docs__auth-pill">{protectedEndpoint ? "API Key · query api_key" : "Identifiants dans le body"}</span><code>{requestUrl}</code></div>

                        <section className="api-docs__doc-group">
                          <div className="api-docs__doc-group-header"><span>01</span><div><h3>Request</h3><p>Headers, paramètres et données à transmettre.</p></div></div>
                          <h4>Headers</h4>
                          <div className="api-docs__field-list">
                            <div className="api-docs__field"><div className="api-docs__field-name"><code>Accept</code><span>string</span><strong>required</strong></div><p>Format attendu pour la réponse.</p><code className="api-docs__field-example">application/json</code></div>
                            {requestExample !== undefined && <div className="api-docs__field"><div className="api-docs__field-name"><code>Content-Type</code><span>string</span><strong>required</strong></div><p>Format du contenu envoyé dans le body.</p><code className="api-docs__field-example">application/json</code></div>}
                          </div>

                          {parameters.length > 0 ? <><h4>Path &amp; query parameters</h4><div className="api-docs__field-list">{parameters.map((parameter, index) => <div className="api-docs__field" key={`${parameter.name}-${index}`}><div className="api-docs__field-name"><code>{parameter.name || "—"}</code><span>{schemaType(parameter.schema, spec)} · {parameter.in || "query"}</span>{parameter.required && <strong>required</strong>}</div><p>{parameter.description || "Paramètre de la requête."}</p><code className="api-docs__field-example">Example: {String(parameterValue(parameter))}</code></div>)}</div></> : <div className="api-docs__no-parameters"><strong>Paramètres :</strong> aucun paramètre métier.</div>}

                          {requestFields.length > 0 && <><h4>Body {endpoint.requestBody?.required && <span className="api-docs__required">REQUIS</span>}</h4><div className="api-docs__field-list">{requestFields.map((field) => <div className="api-docs__field" key={field.name}><div className="api-docs__field-name"><code>{field.name}</code><span>{field.type}</span>{field.required && <strong>required</strong>}</div><p>{field.description}</p></div>)}</div></>}
                        </section>

                        <section className="api-docs__doc-group">
                          <div className="api-docs__doc-group-header"><span>02</span><div><h3>Response</h3><p>Statuts HTTP, headers et structure du résultat.</p></div></div>
                          <h4>HTTP results</h4>
                          <div className="api-docs__responses">{responseEntries.map(({ status, response }) => <span className="api-docs__response" key={status}><strong>{status}</strong>{response.description || "Réponse HTTP"}</span>)}</div>

                          <h4>Headers</h4>
                          <div className="api-docs__field-list"><div className="api-docs__field"><div className="api-docs__field-name"><code>Content-Type</code><span>string</span></div><p>Format de la réponse retournée par l’API.</p><code className="api-docs__field-example">application/json; charset=utf-8</code></div></div>

                          {fields.length > 0 && <><h4>Schema</h4><div className="api-docs__field-list">{fields.map((field) => <div className="api-docs__field" key={field.name}><div className="api-docs__field-name"><code>{field.name}</code><span>{field.type}</span>{field.required && <strong>required</strong>}</div><p>{field.description}</p></div>)}</div></>}
                        </section>
                      </div>

                      <aside className="api-docs__samples">
                        <div className="api-docs__sample-title"><span>Sample request</span><small>{endpoint.method}</small></div>
                        <div className="api-docs__code-card"><div className="api-docs__code-header"><span>cURL</span><CopyButton value={endpointCurl} /></div><pre><code>{endpointCurl}</code></pre></div>

                        <div className="api-docs__sample-title"><span>Sample response</span><small>{responseEntries.find(({ status }) => status.startsWith("2"))?.status || "200"} JSON</small></div>
                        {successExample !== undefined ? <JsonCard label="application/json" value={successExample} /> : <div className="api-docs__sample-empty">Aucun exemple de réponse disponible.</div>}

                        <div className="api-docs__sample-title"><span>Error response</span><small>JSON</small></div>
                        <JsonCard label="application/json" value={{ success: false, code: protectedEndpoint ? "API_KEY_INVALID" : "INVALID_CREDENTIALS", message: protectedEndpoint ? "Invalid API key." : "Invalid username or password." }} />
                      </aside>
                    </div>
                  </article>;
                })}
              </section>;
            })}
            {!filteredEndpoints.length && <div className="api-docs__empty">Aucun endpoint ne correspond à votre recherche.</div>}
          </section>
        </main>

        <footer className="api-docs__footer">© GeoTracking — API V1 · <a href="https://geotrackin.com">geotrackin.com</a></footer>
      </div>
    </div>
  );
};

export default FleetManagementDocumentation;
